/**
 * Arqueo de efectivo por denominación.
 *
 * Cada país cierra en su moneda: el catálogo dice qué billetes y monedas
 * circulan, y con eso el cierre pide cuántas piezas hay de cada una en vez
 * de un solo total escrito a mano. La suma de billetes y monedas es el
 * efectivo contado que se compara contra lo esperado.
 *
 * Sin dependencias de Angular a propósito: se prueba en Node tal cual.
 */

export interface CurrencyProfile {
    /** Código ISO 4217: USD, GTQ, HNL, DOP, COP. */
    code: string;
    /** Símbolo corto, como lo escribe la gente: $, Q, L, RD$. */
    symbol: string;
    /** Nombre en español, para mostrarlo en el cierre. */
    name: string;
    /** Denominaciones de billetes, de mayor a menor. */
    bills: number[];
    /** Denominaciones de monedas, de mayor a menor. */
    coins: number[];
}

/** Moneda tal como queda guardada en el cierre. */
export interface CashCurrency {
    code: string;
    symbol: string;
    name: string;
}

/** Una denominación contada: cuántas piezas hay y cuánto suman. */
export interface CashCountLine {
    value: number;
    /** null = todavía no se anotó; 0 = se contó y no hay. No es lo mismo. */
    qty: number | null;
    subtotal: number;
}

export interface CashCountTotals {
    /** Piezas en total. */
    bills_qty: number;
    coins_qty: number;
    /** Dinero en total. */
    bills_total: number;
    coins_total: number;
    total: number;
}

export interface CashCount extends CashCountTotals {
    currency: CashCurrency;
    bills: CashCountLine[];
    coins: CashCountLine[];
}

// ═══════════════════════════════════════════════════════════════════════════
//  CATÁLOGO
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Billetes y monedas en circulación por moneda. Se incluyen también las
 * denominaciones poco comunes ($2, RD$20, Q1 en billete): una pieza que no
 * se puede anotar obliga a falsear el conteo, y una fila en cero no estorba.
 */
export const CURRENCY_PROFILES: { [code: string]: CurrencyProfile } = {
    USD: {
        code: 'USD', symbol: '$', name: 'Dólar estadounidense',
        bills: [100, 50, 20, 10, 5, 2, 1],
        coins: [1, 0.25, 0.10, 0.05, 0.01],
    },
    GTQ: {
        code: 'GTQ', symbol: 'Q', name: 'Quetzal',
        bills: [200, 100, 50, 20, 10, 5, 1],
        coins: [1, 0.50, 0.25, 0.10, 0.05],
    },
    HNL: {
        code: 'HNL', symbol: 'L', name: 'Lempira',
        bills: [500, 200, 100, 50, 20, 10, 5, 2, 1],
        coins: [0.50, 0.20, 0.10, 0.05],
    },
    DOP: {
        code: 'DOP', symbol: 'RD$', name: 'Peso dominicano',
        bills: [2000, 1000, 500, 200, 100, 50, 20],
        coins: [25, 10, 5, 1],
    },
    COP: {
        code: 'COP', symbol: '$', name: 'Peso colombiano',
        bills: [100000, 50000, 20000, 10000, 5000, 2000, 1000],
        coins: [1000, 500, 200, 100, 50],
    },
};

/** Moneda de cada país donde opera Barlleno. */
export const COUNTRY_CURRENCY: { [countryCode: string]: string } = {
    SV: 'USD',
    GT: 'GTQ',
    HN: 'HNL',
    DO: 'DOP',
    CO: 'COP',
};

/**
 * Moneda con la que arquea una sesión. Manda el país; si el país no está
 * en el catálogo, se intenta con la moneda de la tienda, que puede venir
 * como código ("HNL") o como símbolo ("L"). null = sin catálogo: el cierre
 * pide el total contado a mano.
 */
export function resolveCurrencyProfile(countryCode: string, currencyHint: string): CurrencyProfile | null {
    const byCountry = COUNTRY_CURRENCY[String(countryCode || '').trim().toUpperCase()];
    if (byCountry && CURRENCY_PROFILES[byCountry]) return CURRENCY_PROFILES[byCountry];

    const hint = String(currencyHint || '').trim();
    if (!hint) return null;
    const byCode = CURRENCY_PROFILES[hint.toUpperCase()];
    if (byCode) return byCode;
    const bySymbol = Object.keys(CURRENCY_PROFILES).find(code => CURRENCY_PROFILES[code].symbol === hint);
    return bySymbol ? CURRENCY_PROFILES[bySymbol] : null;
}

// ═══════════════════════════════════════════════════════════════════════════
//  CONTEO
// ═══════════════════════════════════════════════════════════════════════════

function round(n: number): number {
    return Math.round(Number(n || 0) * 100) / 100;
}

/** Cantidad tal como se guarda: entero, nunca negativo; vacío o basura = 0. */
function cleanQty(qty: any): number {
    const n = Math.floor(Number(qty));
    return isFinite(n) && n > 0 ? n : 0;
}

/** true si en esa fila ya se anotó algo (aunque sea 0). */
export function lineTouched(line: CashCountLine): boolean {
    return line.qty !== null && line.qty !== undefined && (line.qty as any) !== '';
}

export function lineSubtotal(line: CashCountLine): number {
    return round(Number(line.value || 0) * cleanQty(line.qty));
}

/** Conteo en blanco para una moneda: todas las denominaciones sin anotar. */
export function newCashCount(profile: CurrencyProfile): CashCount {
    const line = (value: number): CashCountLine => ({ value: value, qty: null, subtotal: 0 });
    return {
        currency: { code: profile.code, symbol: profile.symbol, name: profile.name },
        bills: profile.bills.map(line),
        coins: profile.coins.map(line),
        bills_qty: 0, coins_qty: 0, bills_total: 0, coins_total: 0, total: 0,
    };
}

/**
 * Totales a partir de las cantidades, sin tocar el conteo: se llama desde
 * la pantalla en cada ciclo, y mutar ahí cambiaría valores ya pintados.
 */
export function cashCountTotals(count: CashCount | null): CashCountTotals {
    const totals: CashCountTotals = { bills_qty: 0, coins_qty: 0, bills_total: 0, coins_total: 0, total: 0 };
    if (!count) return totals;
    (count.bills || []).forEach(line => {
        totals.bills_qty += cleanQty(line.qty);
        totals.bills_total = round(totals.bills_total + lineSubtotal(line));
    });
    (count.coins || []).forEach(line => {
        totals.coins_qty += cleanQty(line.qty);
        totals.coins_total = round(totals.coins_total + lineSubtotal(line));
    });
    totals.total = round(totals.bills_total + totals.coins_total);
    return totals;
}

/** Se arqueó si se anotó al menos una denominación (aunque sea en 0). */
export function isCounted(count: CashCount | null): boolean {
    if (!count) return false;
    return (count.bills || []).some(lineTouched) || (count.coins || []).some(lineTouched);
}

/**
 * Copia lista para guardar: cantidades limpias (vacío → 0), subtotales y
 * totales calculados. El conteo en pantalla no se toca.
 */
export function cashCountForSave(count: CashCount): CashCount {
    const clean = (line: CashCountLine): CashCountLine => ({
        value: Number(line.value || 0),
        qty: cleanQty(line.qty),
        subtotal: lineSubtotal(line),
    });
    const totals = cashCountTotals(count);
    return {
        currency: {
            code: count.currency ? String(count.currency.code || '') : '',
            symbol: count.currency ? String(count.currency.symbol || '') : '',
            name: count.currency ? String(count.currency.name || '') : '',
        },
        bills: (count.bills || []).map(clean),
        coins: (count.coins || []).map(clean),
        bills_qty: totals.bills_qty,
        coins_qty: totals.coins_qty,
        bills_total: totals.bills_total,
        coins_total: totals.coins_total,
        total: totals.total,
    };
}

/**
 * Reconstruye un conteo guardado sobre el catálogo de hoy, para editarlo.
 * Las denominaciones del catálogo que no estaban guardadas quedan sin
 * anotar; las guardadas que ya no estén en el catálogo se conservan al
 * final, para que el total siga siendo el que se guardó.
 */
export function restoreCashCount(profile: CurrencyProfile, saved: any): CashCount {
    const count = newCashCount(profile);
    const merge = (lines: CashCountLine[], savedLines: any[]) => {
        (savedLines || []).forEach(s => {
            if (!s) return;
            const value = Number(s.value);
            if (!isFinite(value)) return;
            const found = lines.find(line => line.value === value);
            if (found) {
                found.qty = cleanQty(s.qty);
            } else {
                lines.push({ value: value, qty: cleanQty(s.qty), subtotal: 0 });
            }
        });
    };
    merge(count.bills, saved ? saved.bills : []);
    merge(count.coins, saved ? saved.coins : []);
    count.bills.forEach(line => line.subtotal = lineSubtotal(line));
    count.coins.forEach(line => line.subtotal = lineSubtotal(line));
    const totals = cashCountTotals(count);
    count.bills_qty = totals.bills_qty;
    count.coins_qty = totals.coins_qty;
    count.bills_total = totals.bills_total;
    count.coins_total = totals.coins_total;
    count.total = totals.total;
    return count;
}
